import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEOHead, {
  getWebPageSchema,
  getBreadcrumbSchema,
  getArticleSchema,
} from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, siteInfo } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
          <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
        <p className="text-foreground-500 text-sm mb-4">该漫画不存在或已被下架</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const categoryMap: Record<string, string> = {
    guoman: '国产漫画', rexue: '热血', lianai: '恋爱', xuanhuan: '玄幻',
    wuxia: '武侠', kehuan: '科幻', xuanyi: '悬疑', gaoxiao: '搞笑',
    qihuan: '奇幻', dushi: '都市',
  };

  const sortedChapters = [...comic.chapters].sort((a, b) =>
    sortOrder === 'desc' ? b.chapterNumber - a.chapterNumber : a.chapterNumber - b.chapterNumber,
  );

  const latestChapter = sortedChapters[0];
  const firstChapter = comic.chapters.find((ch) => ch.chapterNumber === 1);

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.categories.some((cat) => comic.categories.includes(cat)))
    .slice(0, 5);

  const handleStartReading = () => {
    if (firstChapter) {
      navigate(`/comic/${comic.id}/chapter/${firstChapter.id}`);
    }
  };

  const handleContinueReading = () => {
    if (latestChapter) {
      navigate(`/comic/${comic.id}/chapter/${latestChapter.id}`);
    }
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${siteInfo.name}`}
        description={`${comic.title}漫画免费阅读，作者${comic.author}，${comic.description.substring(0, 100)}`}
        keywords={`${comic.title},免费阅读,无删减漫画,吹雪漫画网,${comic.tags.join(',')}`}
        canonicalUrl={`https://chuixue.manhua.com/comic/${comic.id}`}
        schemaData={{
          ...getWebPageSchema(
            `${comic.title} - ${siteInfo.name}`,
            comic.description,
            `https://chuixue.manhua.com/comic/${comic.id}`,
          ),
          ...getBreadcrumbSchema([
            { name: '首页', url: 'https://chuixue.manhua.com/' },
            { name: comic.title, url: `https://chuixue.manhua.com/comic/${comic.id}` },
          ]),
          ...getArticleSchema(
            comic.title,
            comic.description,
            `https://chuixue.manhua.com/comic/${comic.id}`,
            comic.author,
            comic.updateTime,
            comic.coverImage,
          ),
        }}
      />

      <Breadcrumb
        items={[
          { name: '首页', href: '/' },
          { name: '漫画分类', href: '/category/all' },
          { name: comic.title },
        ]}
      />

      <article className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
          <div className="shrink-0 mx-auto md:mx-0">
            <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
              <img
                src={comic.coverImage}
                alt={`${comic.title}封面`}
                title={`${comic.title} - 吹雪漫画网免费阅读`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
              {comic.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-sm text-foreground-500">作者：{comic.author}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                comic.status === '连载中'
                  ? 'bg-accent-100 text-accent-700'
                  : 'bg-secondary-100 text-secondary-700'
              }`}>
                {comic.status}
              </span>
              <span className="flex items-center gap-1 text-sm text-accent-500">
                <i className="ri-star-fill"></i>
                {comic.rating}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {comic.categories.map((cat) => (
                <Link
                  key={cat}
                  to={`/category/${cat}`}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {categoryMap[cat] || cat}
                </Link>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {comic.tags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-50 text-secondary-700 text-xs">
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400 mb-5">
              <span>{comic.viewCount > 10000 ? `${(comic.viewCount / 10000).toFixed(0)}万` : comic.viewCount} 次阅读</span>
              <span>{comic.favoriteCount > 10000 ? `${(comic.favoriteCount / 10000).toFixed(0)}万` : comic.favoriteCount} 收藏</span>
              <span>共 {comic.chapters.length} 话</span>
              <span>最近更新：{comic.updateTime}</span>
            </div>

            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              {comic.description}
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleStartReading}
                className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line mr-1.5"></i>
                开始阅读
              </button>
              {firstChapter && latestChapter && latestChapter.chapterNumber > 1 && (
                <button
                  onClick={handleContinueReading}
                  className="px-6 py-2.5 rounded-lg bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  继续阅读（第{latestChapter.chapterNumber}话）
                </button>
              )}
              <button className="px-6 py-2.5 rounded-lg bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-heart-line mr-1.5"></i>
                收藏
              </button>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-lg font-heading text-foreground-900 mb-4">作品简介</h2>
          <p className="text-sm text-foreground-600 leading-relaxed">
            {comic.longDescription}
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading text-foreground-900">
              章节列表（共 {comic.chapters.length} 话）
            </h2>
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
            >
              <i className={`text-xs ${sortOrder === 'desc' ? 'ri-sort-desc' : 'ri-sort-asc'}`}></i>
              {sortOrder === 'desc' ? '最新在前' : '最早在前'}
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {sortedChapters.map((chapter) => (
              <Link
                key={chapter.id}
                to={`/comic/${comic.id}/chapter/${chapter.id}`}
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                  chapter.chapterNumber <= 3
                    ? 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <span className="truncate">{chapter.title}</span>
                {chapter.chapterNumber <= 3 && (
                  <span className="shrink-0 ml-1 px-1.5 py-0.5 rounded bg-primary-500 text-background-50 text-xs">
                    NEW
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>

        {relatedComics.length > 0 && (
          <div className="mt-12">
            <h2 className="text-lg font-heading text-foreground-900 mb-4">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {relatedComics.map((rc) => (
                <Link
                  key={rc.id}
                  to={`/comic/${rc.id}`}
                  className="group cursor-pointer"
                >
                  <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2 bg-background-200">
                    <img
                      src={rc.coverImage}
                      alt={rc.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="text-xs font-medium text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {rc.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
}